import { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Alert, Box, FormGroup, Stack } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import axiosInstance from '../../../utils/axios';
import { APIS } from '../../../utils/serviceUrls';
import { SuccessStory } from '../../../@types/successStory';
import { FormProvider, RHFTextField, RHFSwitch, RHFSelect } from '../../../components/hook-form';
import { UploadMultiFile } from '../../../components/upload';
import TextMaxLine from '../../../components/TextMaxLine';
import { STORY_FORM_FIELDS, SuccessStorySchema, storyFormValues } from './index';
import {
  fieldErrors,
  itemErrorText,
  itemFromConflict,
  patchBody,
} from '../../dashboard/content-order/reorderModel';

type SuccessStoryNewEditFormProps = {
  isEdit: boolean;
  currentStory?: SuccessStory;
  handleClose(): void;
};

const SuccessStoryNewEditForm = ({
  isEdit,
  currentStory,
  handleClose,
}: SuccessStoryNewEditFormProps) => {
  const { defaultValues, NewSuccessStorySchema } = SuccessStorySchema(currentStory);
  const { enqueueSnackbar } = useSnackbar();

  const methods = useForm<any>({
    resolver: yupResolver(NewSuccessStorySchema),
    defaultValues,
  });

  const {
    reset,
    watch,
    setValue,
    setError,
    handleSubmit,
    formState: { isSubmitting, errors },
  } = methods;

  // The story as the form opened it (or the latest version after a 409): the
  // base of the changed-fields PATCH and of its version check.
  const [base, setBase] = useState<SuccessStory | undefined>(currentStory);
  const [formError, setFormError] = useState<string | null>(null);
  useEffect(() => {
    setBase(currentStory);
    setFormError(null);
  }, [currentStory]);

  const values = watch();
  const isActivity = values.type === 'activity';

  const uploadImage = async (file: File) => {
    const form = new FormData();
    form.append('file', file);
    const response = await axiosInstance.post(APIS.UPLOAD.MEDIA, form);
    return response.data;
  };

  // A new story: every filled field, no `order` (the API appends it at the end of its type).
  const createBody = (data: any) => {
    const body = { ...data };
    if (!body.name_ar) delete body.name_ar;
    if (!body.title) delete body.title;
    if (!body.title_ar) delete body.title_ar;
    if (!body.story_ar) delete body.story_ar;
    if (body.type === 'activity' && body.date) body.date = new Date(body.date).toISOString();
    else delete body.date;
    return body;
  };

  // An edit: only what changed, plus `name` + `story` (critic C1) and the
  // version it opened; never `order`. A story keeps its stored date.
  const editBody = (data: any, story: SuccessStory) => {
    const original = storyFormValues(story);
    const edited = { ...data };
    if (edited.type !== 'activity' || !edited.date) edited.date = original.date;
    const body = patchBody('successStory', { ...original, updatedAt: story.updatedAt }, edited);
    if (typeof body.date === 'string' && body.date) body.date = new Date(body.date).toISOString();
    else delete body.date;
    return body;
  };

  const onSubmit = async (data: any) => {
    setFormError(null);
    try {
      if (currentStory && base)
        await axiosInstance.patch(
          APIS.SUCCESS_STORIES.PATCH(currentStory._id as string),
          editBody(data, base)
        );
      else await axiosInstance.post(APIS.SUCCESS_STORIES.ADD, createBody(data));
      reset();
      enqueueSnackbar(!isEdit ? 'Create success!' : 'Update success!');
      handleClose();
    } catch (err) {
      // Someone changed this story meanwhile: their version goes into the form.
      const latest = itemFromConflict<SuccessStory>(err);
      if (latest) {
        setBase(latest);
        reset(storyFormValues(latest));
      }
      fieldErrors(err, STORY_FORM_FIELDS).forEach(({ field, message }) =>
        setError(field, { type: 'server', message })
      );
      const text = itemErrorText(err, !isEdit ? 'Unable to create Story!' : 'Unable to edit Story');
      setFormError(text);
      enqueueSnackbar(text, { variant: 'error' });
    }
  };

  useEffect(() => {
    console.log('Errors', errors);
  }, [errors]);

  return (
    <Stack height={'100%'} style={{ overflow: 'auto' }}>
      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        <TextMaxLine marginBottom={'0px'} marginTop={'0px'} fontSize={'22px'}>
          {isEdit ? ' Edit Success Story' : ' Create Success Story'}
        </TextMaxLine>
        {formError && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setFormError(null)}>
            {formError}
          </Alert>
        )}
        <Stack direction={'column'} justifyContent={'space-between'} height={'auto'}>
          <Stack spacing={3} mt={2}>
            <Box
              sx={{
                display: 'grid',
                columnGap: 1,
                rowGap: 1,
                gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
                alignItems: 'center',
              }}
            >
              <RHFSelect name="type" label="Type">
                <option value="story">Story</option>
                <option value="activity">Activity</option>
              </RHFSelect>
              {isActivity && (
                <RHFTextField
                  name="date"
                  label="Event date"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                />
              )}
            </Box>

            <Box
              sx={{
                display: 'grid',
                columnGap: 1,
                rowGap: 1,
                gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
              }}
            >
              <RHFTextField name="name" label={isActivity ? 'Event title' : 'Name'} />
              <RHFTextField
                name="name_ar"
                label={isActivity ? 'Arabic event title' : 'Arabic Name'}
              />
              <RHFTextField name="title" label={isActivity ? 'Location / tagline' : 'Title'} />
              <RHFTextField
                name="title_ar"
                label={isActivity ? 'Arabic location / tagline' : 'Arabic Title'}
              />
            </Box>

            <FormGroup
              sx={{
                display: 'grid',
                columnGap: 1,
                rowGap: 1,
              }}
            >
              <RHFTextField multiline minRows={4} maxRows={8} name="story" label="Story" />
              <RHFTextField
                multiline
                minRows={4}
                maxRows={8}
                name="story_ar"
                label="Arabic Story"
              />
            </FormGroup>

            <Box
              sx={{
                display: 'grid',
                columnGap: 2,
                rowGap: 1,
                gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
                alignItems: 'center',
              }}
            >
              <RHFSwitch name="active" label="Active" />
            </Box>

            <Stack spacing={1}>
              <TextMaxLine sx={{ fontSize: '16px', fontWeight: '700' }}>Images</TextMaxLine>
              <UploadMultiFile
                accept={{ 'image/*': [] }}
                files={values.images || []}
                label={'Select Or Drop Images '}
                error={!!errors.images}
                onDropAccepted={async (files) => {
                  const res = await uploadImage(files[0]);
                  setValue('images', [...(values.images || []), res?.url]);
                }}
                onRemove={(file) =>
                  setValue(
                    'images',
                    (values.images || []).filter((u: string) => u !== file)
                  )
                }
              />
            </Stack>
          </Stack>

          <Stack alignItems="flex-end" sx={{ mt: 3 }}>
            <LoadingButton
              style={{ width: '200px', height: '50px' }}
              type="submit"
              variant="contained"
              loading={isSubmitting}
            >
              {!isEdit ? 'Create Story' : 'Save Changes'}
            </LoadingButton>
          </Stack>
        </Stack>
      </FormProvider>
    </Stack>
  );
};

export default SuccessStoryNewEditForm;
